/**
 * SchemaTab — Schema builder tab content
 * Palantir-style interface: toolbar at top + collapsible editing sidebar + graph
 */

import {
  Background,
  type Connection,
  Controls,
  type Edge,
  type Node,
  type NodeTypes,
  type OnEdgesChange,
  type OnNodesChange,
  ReactFlow,
} from '@xyflow/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTheme } from '@/theme'
import '@xyflow/react/dist/style.css'
import {
  AlertTriangle,
  Anchor,
  Box,
  ChevronDown,
  ChevronRight,
  GitMerge,
  Info,
  Loader2,
  Plus,
  Save,
  Sparkles,
  TableProperties,
  Target,
  Trash2,
  Wand2,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { FilterableCombobox } from '@/components/ui/filterable-combobox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { toast } from '@/components/ui/sonner'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type {
  ConditionLogic,
  ConditionOperator,
  RowCondition,
  RowConditionClause,
} from '@/services/api/cases'
import type { TabularColumn } from '@/services/api/lake'
import type { Schema } from '@/services/api/schemas'
import { cn } from '@/utils/cn'
import { getColumnSamples } from './helpers'
import { RelationshipEditor } from './RelationshipEditor'
import type { BuiltEntity, BuiltRelationship } from './types'

// ---------------------------------------------------------------------------
// Operator labels (for row conditions)
// ---------------------------------------------------------------------------

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  non_empty: 'is not empty',
  empty: 'is empty',
  equals: '=',
  not_equals: '≠',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
}

const OPERATORS_WITHOUT_VALUE: ConditionOperator[] = ['non_empty', 'empty']

// ---------------------------------------------------------------------------
// RowConditionBuilder — inline component for sidebar
// ---------------------------------------------------------------------------

interface RowConditionBuilderProps {
  availableColumns: string[]
  value: RowCondition | null
  onChange: (condition: RowCondition | null) => void
  pickingContext: import('./types').PickingContext
  onStartPickingColumn: (clauseIndex: number) => void
  onStartPickingValue: (clauseIndex: number) => void
}

function RowConditionBuilder({
  availableColumns,
  value,
  onChange,
  pickingContext,
  onStartPickingColumn,
  onStartPickingValue,
}: RowConditionBuilderProps) {
  const [expanded, setExpanded] = useState(false)

  const enabled = value !== null
  const condition = value ?? { logic: 'and' as ConditionLogic, clauses: [] }

  const toggle = () => {
    if (enabled) {
      onChange(null)
      setExpanded(false)
    } else {
      onChange({ logic: 'and', clauses: [] })
      setExpanded(true)
    }
  }

  const addClause = () => {
    onChange({
      ...condition,
      clauses: [...condition.clauses, { column: '', operator: 'non_empty', value: '' }],
    })
  }

  const removeClause = (i: number) => {
    const clauses = condition.clauses.filter((_, idx) => idx !== i)
    onChange({ ...condition, clauses })
  }

  const updateClause = (i: number, patch: Partial<RowConditionClause>) => {
    const clauses = condition.clauses.map((c, idx) => (idx === i ? { ...c, ...patch } : c))
    onChange({ ...condition, clauses })
  }

  return (
    <div className="border rounded-md overflow-hidden">
      {/* Header row */}
      <button
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium bg-muted/40 hover:bg-muted/60 transition-colors"
        onClick={() => {
          if (!enabled) toggle()
          else setExpanded(e => !e)
        }}
      >
        {enabled ? (
          expanded ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground" />
        )}
        <span className={enabled ? 'text-foreground' : 'text-muted-foreground'}>
          Row Condition
          {enabled && condition.clauses.length > 0 && (
            <span className="ml-1.5 text-primary">
              ({condition.clauses.length} clause{condition.clauses.length !== 1 ? 's' : ''})
            </span>
          )}
        </span>
        {!enabled && (
          <span className="ml-auto text-[10px] text-muted-foreground">click to enable</span>
        )}
        {enabled && (
          <button
            type="button"
            className="ml-auto text-[10px] text-destructive hover:underline"
            onClick={e => {
              e.stopPropagation()
              toggle()
            }}
          >
            remove
          </button>
        )}
      </button>

      {/* Body */}
      {enabled && expanded && (
        <div className="px-3 py-2 space-y-2">
          {/* Logic selector */}
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Combine clauses with</span>
            <Select
              value={condition.logic}
              onValueChange={v => onChange({ ...condition, logic: v as ConditionLogic })}
            >
              <SelectTrigger className="h-6 w-16 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[250]">
                <SelectItem value="and">AND</SelectItem>
                <SelectItem value="or">OR</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Clauses */}
          {condition.clauses.map((clause, i) => {
            const needsValue = !OPERATORS_WITHOUT_VALUE.includes(clause.operator)
            return (
              <div key={i} className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  {/* Column picker - TARGET ONLY */}
                  <Button
                    type="button"
                    size="sm"
                    variant={
                      pickingContext?.type === 'row-condition-column' &&
                      pickingContext.clauseIndex === i
                        ? 'default'
                        : 'outline'
                    }
                    onClick={() => onStartPickingColumn(i)}
                    className="flex-1 justify-start text-xs h-8"
                    title="Pick column from data preview"
                  >
                    {pickingContext?.type === 'row-condition-column' &&
                    pickingContext.clauseIndex === i ? (
                      <>
                        <Target className="h-3 w-3 mr-1.5" />
                        <span className="text-primary">Pick column...</span>
                      </>
                    ) : clause.column ? (
                      <>
                        <Target className="h-3 w-3 mr-1.5 opacity-50" />
                        <span className="truncate">{clause.column}</span>
                      </>
                    ) : (
                      <>
                        <Target className="h-3 w-3 mr-1.5" />
                        Click to pick
                      </>
                    )}
                  </Button>

                  {/* Operator select */}
                  <Select
                    value={clause.operator}
                    onValueChange={v =>
                      updateClause(i, { operator: v as ConditionOperator, value: undefined })
                    }
                  >
                    <SelectTrigger className="h-8 text-xs w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[250]">
                      {(Object.keys(OPERATOR_LABELS) as ConditionOperator[]).map(op => (
                        <SelectItem key={op} value={op}>
                          {OPERATOR_LABELS[op]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Remove clause button */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => removeClause(i)}
                    title="Remove clause"
                  >
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                </div>

                {/* Value picker row (only for operators that need values) */}
                {needsValue && (
                  <div className="flex items-center gap-1.5 pl-1">
                    <span className="text-xs text-muted-foreground w-12 shrink-0">Value:</span>
                    <Button
                      type="button"
                      size="sm"
                      variant={
                        pickingContext?.type === 'row-condition-value' &&
                        pickingContext.clauseIndex === i
                          ? 'default'
                          : 'outline'
                      }
                      onClick={() => onStartPickingValue(i)}
                      className="flex-1 justify-start text-xs h-7"
                      title="Pick value from data preview"
                    >
                      {pickingContext?.type === 'row-condition-value' &&
                      pickingContext.clauseIndex === i ? (
                        <>
                          <Target className="h-3 w-3 mr-1.5" />
                          <span className="text-primary">Pick value...</span>
                        </>
                      ) : clause.value ? (
                        <>
                          <Target className="h-3 w-3 mr-1.5 opacity-50" />
                          <span className="truncate">{clause.value}</span>
                        </>
                      ) : (
                        <>
                          <Target className="h-3 w-3 mr-1.5" />
                          Click to pick
                        </>
                      )}
                    </Button>

                    {/* Clear value button */}
                    {clause.value && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() => updateClause(i, { value: '' })}
                        title="Clear value"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 text-xs w-full"
            onClick={addClause}
            disabled={availableColumns.length === 0}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add clause
          </Button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Row Condition Tooltip SVG Diagram
// ---------------------------------------------------------------------------

function RowConditionTooltipContent() {
  return (
    <div className="max-w-xs space-y-2">
      <p className="text-xs font-semibold">What are Row Conditions?</p>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Row conditions filter which spreadsheet rows create this entity. Only rows that match all
        conditions will generate an entity node.
      </p>

      {/* Simple diagram */}
      <div className="bg-muted/30 rounded p-3 space-y-2">
        <svg width="100%" height="80" viewBox="0 0 240 80" className="text-foreground">
          {/* All rows */}
          <g>
            <rect x="10" y="5" width="60" height="8" fill="currentColor" opacity="0.3" rx="1" />
            <rect x="10" y="16" width="60" height="8" fill="currentColor" opacity="0.3" rx="1" />
            <rect x="10" y="27" width="60" height="8" fill="currentColor" opacity="0.3" rx="1" />
            <rect x="10" y="38" width="60" height="8" fill="currentColor" opacity="0.3" rx="1" />
            <rect x="10" y="49" width="60" height="8" fill="currentColor" opacity="0.3" rx="1" />
            <text x="40" y="70" fontSize="9" textAnchor="middle" opacity="0.6">
              All Rows
            </text>
          </g>

          {/* Arrow + filter icon */}
          <g>
            <path d="M 75 30 L 95 30" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
            <polygon points="95,30 90,27 90,33" fill="currentColor" opacity="0.5" />
            <circle
              cx="105"
              cy="30"
              r="10"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              opacity="0.6"
            />
            <path
              d="M 100 25 L 110 35 M 102 30 L 108 30"
              stroke="currentColor"
              strokeWidth="1.5"
              opacity="0.6"
            />
            <text x="105" y="70" fontSize="9" textAnchor="middle" opacity="0.6">
              Filter
            </text>
          </g>

          {/* Arrow */}
          <g>
            <path d="M 120 30 L 145 30" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
            <polygon points="145,30 140,27 140,33" fill="currentColor" opacity="0.5" />
          </g>

          {/* Filtered rows (fewer) */}
          <g>
            <rect
              x="170"
              y="12"
              width="60"
              height="8"
              fill="hsl(var(--primary))"
              opacity="0.7"
              rx="1"
            />
            <rect
              x="170"
              y="27"
              width="60"
              height="8"
              fill="hsl(var(--primary))"
              opacity="0.7"
              rx="1"
            />
            <rect
              x="170"
              y="42"
              width="60"
              height="8"
              fill="hsl(var(--primary))"
              opacity="0.7"
              rx="1"
            />
            <text x="200" y="70" fontSize="9" textAnchor="middle" opacity="0.6">
              Matching Rows
            </text>
          </g>
        </svg>
      </div>

      <p className="text-[10px] text-muted-foreground italic">
        Example: "Status = Active" creates entities only for active records
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SchemaTabProps {
  // Schema data
  loadingSchema: boolean
  schema: Schema | null
  entities: BuiltEntity[]
  relationships: BuiltRelationship[]
  selectedEntityId: string | null
  selectedRelationshipId: string | null
  rightPanelVisible: boolean

  // Graph state
  nodes: Node[]
  edges: Edge[]
  nodeTypes: NodeTypes
  onNodesChange: OnNodesChange
  onEdgesChange: OnEdgesChange

  // Tabular data for column samples
  availableColumns: string[]
  tabularData: { columns: TabularColumn[]; rows: (string | number | boolean | null)[][] } | null

  // Handlers
  onAddEntity: (
    entityType: string,
    label: string,
    identifierColumn: string | null,
    propertyMappings: Record<string, string>,
    rowCondition: RowCondition | null,
    isAnchor?: boolean
  ) => void
  onDeleteEntity: (id: string) => void
  onCreateRelationship: (sourceEntityId: string, targetEntityId: string) => void
  onNodeClick: (_event: React.MouseEvent, node: Node) => void
  onEdgeClick: (_event: React.MouseEvent, edge: Edge) => void
  onSetSelectedEntityId: (id: string | null) => void
  onSetRightPanelVisible: (visible: boolean) => void
  onPropertyMapping: (propertyName: string, columnName: string | null) => void
  onSetIdentifier: (columnName: string | null) => void
  onUpdateRelationship: (
    relationshipId: string,
    relationshipType: string,
    propertyMappings: Record<string, string>
  ) => void
  onDeleteRelationship: (relationshipId: string) => void

  // Column picking mode
  pickingContext: import('./types').PickingContext
  onStartPickingColumn: (propertyName: string) => void
  // Ref for parent to update local editing state when visual picker is used
  updateLocalPropertyMappingRef?: React.MutableRefObject<
    ((propertyName: string, columnName: string) => void) | null
  >
  // Ref for parent to update local row condition state when visual picker is used
  updateLocalRowConditionRef?: React.MutableRefObject<
    ((clauseIndex: number, field: 'column' | 'value', newValue: string) => void) | null
  >
  onStartPickingRowConditionColumn: (clauseIndex: number) => void
  onStartPickingRowConditionValue: (clauseIndex: number) => void

  // Sidebar resize
  sidebarWidth?: number
  onSidebarResize?: (e: React.MouseEvent<HTMLDivElement>) => void

  // Expose save function for parent to call before switching entities
  saveCurrentEntityRef?: React.MutableRefObject<(() => boolean) | null>

  // Auto-layout
  onAutoLayout: () => void
}

// ---------------------------------------------------------------------------
// SchemaTab Component
// ---------------------------------------------------------------------------

export function SchemaTab({
  loadingSchema,
  schema,
  entities,
  relationships,
  selectedEntityId,
  selectedRelationshipId,
  rightPanelVisible,
  nodes,
  edges,
  nodeTypes,
  onNodesChange,
  onEdgesChange,
  availableColumns,
  tabularData,
  onAddEntity,
  onDeleteEntity,
  onCreateRelationship,
  onNodeClick,
  onEdgeClick,
  onSetSelectedEntityId,
  onSetRightPanelVisible,
  onPropertyMapping,
  onSetIdentifier,
  onUpdateRelationship,
  onDeleteRelationship,
  pickingContext,
  onStartPickingColumn,
  updateLocalPropertyMappingRef,
  updateLocalRowConditionRef,
  onStartPickingRowConditionColumn,
  onStartPickingRowConditionValue,
  sidebarWidth = 432, // 30% of 1440px screen as fallback
  onSidebarResize,
  saveCurrentEntityRef,
  onAutoLayout,
}: SchemaTabProps) {
  // Theme support
  const { theme: themeRaw } = useTheme()
  const resolvedTheme = useMemo(() => {
    if (themeRaw === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return themeRaw
  }, [themeRaw])

  // Local state for editing the selected entity
  const [editingEntityType, setEditingEntityType] = useState<string>('')
  const [editingLabel, setEditingLabel] = useState<string>('')
  const [editingIdentifier, setEditingIdentifier] = useState<string | null>(null)
  const [editingPropertyMappings, setEditingPropertyMappings] = useState<Record<string, string>>({})
  const [editingRowCondition, setEditingRowCondition] = useState<RowCondition | null>(null)
  const [editingIsAnchor, setEditingIsAnchor] = useState<boolean>(false)

  // Local state for editing the selected relationship
  const [editingRelationshipType, setEditingRelationshipType] = useState<string>('')
  const [editingRelPropertyMappings, setEditingRelPropertyMappings] = useState<
    Record<string, string>
  >({})
  const [editingSourceEntityId, setEditingSourceEntityId] = useState<string>('')
  const [editingTargetEntityId, setEditingTargetEntityId] = useState<string>('')

  const selectedEntity = entities.find(e => e.id === selectedEntityId)
  const selectedEntityType = schema?.entity_types.find(et => et.label === editingEntityType)

  // Check if there's already an anchor node
  const hasAnchorNode = useMemo(() => entities.some(e => e.isAnchor), [entities])

  // Track if there are unsaved changes (dirty state)
  const isDirty = useMemo(() => {
    if (!selectedEntity) return false
    return (
      selectedEntity.entityType !== editingEntityType ||
      selectedEntity.label !== editingLabel ||
      selectedEntity.identifierColumn !== editingIdentifier ||
      JSON.stringify(selectedEntity.columnMappings) !== JSON.stringify(editingPropertyMappings) ||
      JSON.stringify(selectedEntity.rowCondition) !== JSON.stringify(editingRowCondition) ||
      (selectedEntity.isAnchor ?? false) !== editingIsAnchor
    )
  }, [
    selectedEntity,
    editingEntityType,
    editingLabel,
    editingIdentifier,
    editingPropertyMappings,
    editingRowCondition,
    editingIsAnchor,
  ])

  // Sync local editing state when selected entity ID changes (not on every parent state update)
  useEffect(() => {
    if (selectedEntity) {
      setEditingEntityType(selectedEntity.entityType)
      setEditingLabel(selectedEntity.label)
      setEditingIdentifier(selectedEntity.identifierColumn)
      setEditingPropertyMappings(selectedEntity.columnMappings)
      setEditingRowCondition(selectedEntity.rowCondition)
      setEditingIsAnchor(selectedEntity.isAnchor ?? false)
    }
  }, [selectedEntity.label, selectedEntity.identifierColumn, selectedEntity])

  // Sync local editing state when selected relationship changes
  useEffect(() => {
    if (selectedRelationshipId) {
      const rel = relationships.find(r => r.id === selectedRelationshipId)
      if (rel) {
        setEditingRelationshipType(rel.relationshipType)
        setEditingRelPropertyMappings(rel.columnMappings)
        setEditingSourceEntityId(rel.sourceEntityId)
        setEditingTargetEntityId(rel.targetEntityId)
      }
    } else {
      // Clear when deselected
      setEditingRelationshipType('')
      setEditingRelPropertyMappings({})
      setEditingSourceEntityId('')
      setEditingTargetEntityId('')
    }
  }, [selectedRelationshipId, relationships])

  // Apply changes to parent - returns true if successful, false if validation failed
  const applyChanges = (): boolean => {
    if (!selectedEntityId || !selectedEntity) return false

    // Validate required fields
    if (!editingEntityType) {
      toast.error('Please select an entity type')
      return false
    }
    if (!editingLabel.trim()) {
      toast.error('Please provide a label')
      return false
    }

    // Check required properties
    const entityType = schema?.entity_types.find(et => et.label === editingEntityType)
    const requiredProps = entityType?.properties.filter(p => p.required) || []
    const unmappedRequired = requiredProps.filter(p => !editingPropertyMappings[p.name])
    if (unmappedRequired.length > 0) {
      toast.error(`Please map required properties: ${unmappedRequired.map(p => p.name).join(', ')}`)
      return false
    }

    // If everything is valid, call onAddEntity to update
    onAddEntity(
      editingEntityType,
      editingLabel.trim(),
      editingIdentifier,
      editingPropertyMappings,
      editingRowCondition,
      editingIsAnchor
    )
    return true
  }

  // Apply relationship changes - returns true if successful, false if validation failed
  const applyRelationshipChanges = useCallback((): boolean => {
    if (!selectedRelationshipId) return false

    // Validation: Check if relationship type is still selected
    if (!editingRelationshipType) {
      toast.error('Relationship type is required')
      return false
    }

    // Find the relationship type definition to check required properties
    const relTypeDef = schema?.relationship_types.find(rt => rt.label === editingRelationshipType)
    if (relTypeDef) {
      const requiredProps = relTypeDef.properties?.filter(p => p.required) || []
      for (const prop of requiredProps) {
        if (!editingRelPropertyMappings[prop.name]) {
          toast.error(`Required property "${prop.name}" must be mapped`)
          return false
        }
      }
    }

    // Update parent state
    onUpdateRelationship(
      selectedRelationshipId,
      editingRelationshipType,
      editingRelPropertyMappings
    )

    return true
  }, [
    selectedRelationshipId,
    editingRelationshipType,
    editingRelPropertyMappings,
    schema?.relationship_types,
    onUpdateRelationship,
  ])

  // Expose save function to parent via ref so parent can trigger save before switching
  useEffect(() => {
    if (saveCurrentEntityRef) {
      saveCurrentEntityRef.current = selectedEntityId ? applyChanges : applyRelationshipChanges
    }
  }, [saveCurrentEntityRef, applyChanges, applyRelationshipChanges, selectedEntityId])

  // Handle connection: create relationship with empty type and open sidebar
  const handleConnect = (connection: Connection) => {
    if (!connection.source || !connection.target) return
    onCreateRelationship(connection.source, connection.target)
  }

  const handleAddEntity = (type: 'standard' | 'anchor') => {
    const isAnchor = type === 'anchor'
    const label = isAnchor ? 'Anchor Node' : 'New Entity'

    onAddEntity('', label, null, {}, null, isAnchor)

    setTimeout(() => {
      onSetRightPanelVisible(true)
    }, 50)
  }

  const handleCloseEditPanel = () => {
    if (selectedEntityId) {
      applyChanges()
    } else if (selectedRelationshipId) {
      applyRelationshipChanges()
    }
    onSetRightPanelVisible(false)
    onSetSelectedEntityId(null)
  }

  const handlePropertyMappingLocal = (propertyName: string, columnName: string | null) => {
    setEditingPropertyMappings(prev => {
      if (columnName === null) {
        const { [propertyName]: _, ...rest } = prev
        return rest
      }
      return { ...prev, [propertyName]: columnName }
    })
    // DO NOT update parent immediately - keep changes local until save
  }

  const handleRelPropertyMappingLocal = (propertyName: string, columnName: string | null) => {
    setEditingRelPropertyMappings(prev => {
      if (columnName === null) {
        const { [propertyName]: _, ...rest } = prev
        return rest
      }
      return { ...prev, [propertyName]: columnName }
    })
  }

  // Handle relationship type change - also clears property mappings since properties differ per type
  const handleRelTypeChangeLocal = useCallback((newType: string) => {
    setEditingRelationshipType(newType)
    // Clear property mappings when type changes (properties are different per type)
    setEditingRelPropertyMappings({})
  }, [])

  const handleDeleteRelationship = () => {
    if (!selectedRelationshipId) return

    if (confirm('Delete this relationship? This action cannot be undone.')) {
      onDeleteRelationship(selectedRelationshipId)
      onSetRightPanelVisible(false)
      toast.success('Relationship deleted')
    }
  }

  const handleSetIdentifierLocal = (columnName: string | null) => {
    setEditingIdentifier(columnName)
    // DO NOT update parent immediately - keep changes local until save
  }

  // Expose function to update local property mappings via ref
  // Parent calls this when visual picker selects a column
  const updateLocalPropertyMapping = useCallback((propertyName: string, columnName: string) => {
    setEditingPropertyMappings(prev => ({
      ...prev,
      [propertyName]: columnName,
    }))
  }, [])

  useEffect(() => {
    if (updateLocalPropertyMappingRef) {
      updateLocalPropertyMappingRef.current = updateLocalPropertyMapping
    }
  }, [updateLocalPropertyMappingRef, updateLocalPropertyMapping])

  // Expose function to update local row condition via ref
  // Parent calls this when visual picker selects a column or value
  const updateLocalRowCondition = useCallback(
    (clauseIndex: number, field: 'column' | 'value', newValue: string) => {
      setEditingRowCondition(prev => {
        if (!prev) return prev
        const updatedClauses = prev.clauses.map((clause, i) =>
          i === clauseIndex ? { ...clause, [field]: newValue } : clause
        )
        return { ...prev, clauses: updatedClauses }
      })
    },
    []
  )

  useEffect(() => {
    if (updateLocalRowConditionRef) {
      updateLocalRowConditionRef.current = updateLocalRowCondition
    }
  }, [updateLocalRowConditionRef, updateLocalRowCondition])

  const handleEntityTypeChange = (newType: string) => {
    setEditingEntityType(newType)
    // Reset property mappings when type changes
    setEditingPropertyMappings({})
  }

  if (loadingSchema) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!schema) {
    return (
      <div className="flex-1 flex items-center justify-center text-center p-4">
        <div className="space-y-2">
          <AlertTriangle className="h-8 w-8 mx-auto text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">No active extraction schema found</p>
        </div>
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div className="flex-1 flex overflow-hidden">
        {/* Left Editing Panel (collapsible) */}
        {rightPanelVisible && (selectedEntity || selectedRelationshipId) && (
          <div
            className="border-r flex flex-col bg-muted/30 relative"
            style={{ width: `${sidebarWidth}px` }}
          >
            {/* Panel Header with Save and Close Buttons */}
            <div className="px-3 py-2.5 border-b flex items-center justify-between bg-muted/50 flex-shrink-0">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                {selectedEntity ? (
                  <>
                    <TableProperties className="h-3.5 w-3.5" />
                    Edit Entity
                  </>
                ) : (
                  <>
                    <GitMerge className="h-3.5 w-3.5" />
                    Edit Relationship
                  </>
                )}
                {isDirty && (
                  <span className="text-orange-500 text-sm" title="Unsaved changes">
                    *
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={selectedEntity ? applyChanges : applyRelationshipChanges}
                      disabled={!isDirty}
                    >
                      <Save className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Save changes</TooltipContent>
                </Tooltip>
                <Button variant="ghost" size="icon-sm" onClick={handleCloseEditPanel}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>

            {/* Entity Info Header */}
            {selectedEntity && (
              <div className="px-3 py-2 border-b bg-background flex-shrink-0">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: selectedEntity.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium truncate">{selectedEntity.label}</span>
                      {editingIsAnchor && (
                        <Anchor className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {selectedEntity.entityType || '(no type selected)'}
                      {editingIsAnchor && ' • Anchor Node'}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onDeleteEntity(selectedEntity.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            )}

            {/* Scrollable content area */}
            <div className="flex-1 relative overflow-hidden">
              <ScrollArea className="h-full">
                {selectedEntity ? (
                  <div className="p-3 space-y-4 pb-6">
                    {/* Entity Type */}
                    <div>
                      <Label className="text-sm mb-2 block">
                        Entity Type <span className="text-destructive">*</span>
                      </Label>
                      <FilterableCombobox
                        value={editingEntityType}
                        onValueChange={handleEntityTypeChange}
                        items={schema.entity_types.map(et => ({
                          value: et.label,
                          label: et.label,
                          description: et.description,
                        }))}
                        placeholder="Select entity type..."
                        searchPlaceholder="Search entity types..."
                      />
                      {editingEntityType && selectedEntityType?.description && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {selectedEntityType.description}
                        </p>
                      )}
                    </div>

                    {/* Custom Label */}
                    <div>
                      <Label className="text-sm mb-2 block">
                        Custom Label <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        value={editingLabel}
                        onChange={e => setEditingLabel(e.target.value)}
                        placeholder="e.g. Caller, Receiver, Account Holder"
                        className="h-8"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Give this entity a meaningful name for this dataset
                      </p>
                    </div>

                    {editingEntityType && selectedEntityType && (
                      <>
                        <Separator />

                        {/* Identifier Column */}
                        <div>
                          <Label className="text-sm mb-2 block">Identifier Column</Label>
                          <FilterableCombobox
                            value={editingIdentifier || '__none__'}
                            onValueChange={val =>
                              handleSetIdentifierLocal(val === '__none__' ? null : val)
                            }
                            items={[
                              { value: '__none__', label: '(none)' },
                              ...availableColumns.map(col => {
                                const samples = tabularData
                                  ? getColumnSamples(col, tabularData.columns, tabularData.rows, 2)
                                  : []
                                return {
                                  value: col,
                                  label: col,
                                  meta:
                                    samples.length > 0 ? `e.g., ${samples.join(', ')}` : undefined,
                                }
                              }),
                            ]}
                            placeholder="(optional)"
                            searchPlaceholder="Search columns..."
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Column that uniquely identifies each instance of this entity
                          </p>
                        </div>

                        <Separator />

                        {/* Property Mappings */}
                        <div>
                          <Label className="text-sm mb-2 block font-semibold">
                            Property Mappings
                          </Label>
                          {selectedEntityType.properties.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                              This entity type has no properties to map.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {selectedEntityType.properties.map(prop => {
                                const isActive =
                                  pickingContext?.type === 'property' &&
                                  pickingContext.propertyName === prop.name
                                return (
                                  <div key={prop.name}>
                                    <Label className="text-xs text-muted-foreground mb-1 block">
                                      {prop.name}
                                      {prop.required && (
                                        <span className="text-destructive ml-0.5">*</span>
                                      )}
                                    </Label>
                                    <div className="flex gap-1.5 items-center">
                                      {/* Display current selection */}
                                      <div
                                        className={cn(
                                          'flex-1 h-8 px-3 py-2 border rounded-md bg-background text-sm flex items-center',
                                          isActive && 'ring-2 ring-primary'
                                        )}
                                      >
                                        {editingPropertyMappings[prop.name] ? (
                                          <span className="truncate">
                                            {editingPropertyMappings[prop.name]}
                                          </span>
                                        ) : (
                                          <span className="text-muted-foreground text-xs">
                                            {isActive
                                              ? 'Pick column below...'
                                              : 'Click target to select'}
                                          </span>
                                        )}
                                      </div>

                                      {/* Pick from preview button */}
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant={isActive ? 'default' : 'outline'}
                                            size="sm"
                                            className="h-8 w-8 p-0"
                                            onClick={() => onStartPickingColumn(prop.name)}
                                          >
                                            <Target className="h-3.5 w-3.5" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="right">
                                          Pick column from data preview
                                        </TooltipContent>
                                      </Tooltip>

                                      {/* Clear button (if mapped) */}
                                      {editingPropertyMappings[prop.name] && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0"
                                          onClick={() =>
                                            handlePropertyMappingLocal(prop.name, null)
                                          }
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      )}
                                    </div>
                                    {prop.description && (
                                      <p className="text-[10px] text-muted-foreground mt-0.5">
                                        {prop.description}
                                      </p>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>

                        <Separator />

                        {/* Row Condition */}
                        {availableColumns.length > 0 && (
                          <div>
                            <div className="flex items-center gap-1.5 mb-2">
                              <Label className="text-sm font-semibold">Row Condition</Label>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-xs">
                                  <RowConditionTooltipContent />
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2 max-w-full break-words">
                              Create entity only when conditions are met. Leave disabled for all
                              rows.
                            </p>
                            <RowConditionBuilder
                              availableColumns={availableColumns}
                              value={editingRowCondition}
                              onChange={setEditingRowCondition}
                              pickingContext={pickingContext}
                              onStartPickingColumn={onStartPickingRowConditionColumn}
                              onStartPickingValue={onStartPickingRowConditionValue}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : selectedRelationshipId && schema ? (
                  <RelationshipEditor
                    relationshipType={editingRelationshipType}
                    sourceEntityId={editingSourceEntityId}
                    targetEntityId={editingTargetEntityId}
                    propertyMappings={editingRelPropertyMappings}
                    relationshipTypes={schema.relationship_types}
                    entities={entities}
                    availableColumns={availableColumns}
                    tabularData={tabularData}
                    onRelationshipTypeChange={handleRelTypeChangeLocal}
                    onPropertyMappingChange={handleRelPropertyMappingLocal}
                    onDeleteRelationship={handleDeleteRelationship}
                  />
                ) : null}
              </ScrollArea>

              {/* Fade indicator at bottom when content is scrollable */}
              <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-muted/50 to-transparent pointer-events-none" />
            </div>
          </div>
        )}

        {/* Vertical resize handle */}
        {rightPanelVisible && (selectedEntity || selectedRelationshipId) && onSidebarResize && (
          <div
            className="w-1.5 bg-border hover:bg-primary/40 cursor-col-resize shrink-0 transition-colors"
            onMouseDown={onSidebarResize}
          />
        )}

        {/* Center — Graph */}
        <div className="flex-1 bg-muted relative">
          {/* Floating Add Entity Button */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="default"
                className="absolute top-4 left-4 z-10 rounded-full shadow-lg h-9 w-9"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="start" className="w-48 z-[250]">
              <DropdownMenuItem onClick={() => handleAddEntity('standard')}>
                <Box className="h-4 w-4 mr-2 text-blue-500" />
                Standard Entity
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleAddEntity('anchor')} disabled={hasAnchorNode}>
                <Anchor className="h-4 w-4 mr-2 text-amber-500" />
                Anchor Node
                {hasAnchorNode && (
                  <span className="ml-auto text-[10px] text-muted-foreground">limit 1</span>
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Auto Layout Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                className="absolute top-4 left-14 z-10 rounded-full shadow-lg h-9 w-9 bg-card"
                onClick={onAutoLayout}
              >
                <Wand2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Auto Layout</p>
            </TooltipContent>
          </Tooltip>

          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={handleConnect}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            proOptions={{ hideAttribution: true }}
            minZoom={0.5}
            maxZoom={1.5}
            nodesDraggable={true}
            nodesConnectable={true}
            elementsSelectable={true}
          >
            <Background
              color={resolvedTheme === 'dark' ? '#262626' : '#cbd5e1'}
              gap={20}
              size={1}
            />
            <Controls className="!bg-card !border-border !rounded-lg [&>button]:!bg-card [&>button]:!border-border [&>button]:!text-muted-foreground [&>button:hover]:!bg-accent" />
          </ReactFlow>

          {/* Empty-state help overlay */}
          {entities.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Card variant="floating" className="rounded-xl p-7 max-w-sm w-full mx-6 shadow-2xl">
                <div className="flex flex-col items-center text-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary/15 flex items-center justify-center">
                    <TableProperties className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-1">
                      Extraction Schema Builder
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Map spreadsheet columns to knowledge graph nodes and edges so the extraction
                      pipeline knows what to pull out of your tabular data.
                    </p>
                  </div>
                  <div className="w-full space-y-3 text-left">
                    <div className="flex items-start gap-3">
                      <div className="h-6 w-6 rounded-md bg-muted border border-border flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Plus className="h-3 w-3 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-foreground">Entities</p>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          Node types such as Person or Company. Each entity maps one or more
                          spreadsheet columns to its properties (e.g. "Name" → name).
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="h-6 w-6 rounded-md bg-muted border border-border flex items-center justify-center flex-shrink-0 mt-0.5">
                        <GitMerge className="h-3 w-3 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-foreground">Relationships</p>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          Drag from one entity node to another to define a directed edge type
                          between them (e.g. Person → WORKS_AT → Company).
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="h-6 w-6 rounded-md bg-muted border border-border flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Sparkles className="h-3 w-3 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-foreground">Result</p>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          The saved schema drives the extraction pipeline, turning each row of your
                          spreadsheet into nodes and edges in the knowledge graph.
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Click the "+" button to add your first entity.
                  </p>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}
